import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Terminal } from 'xterm';
import { FitAddon } from 'xterm-addon-fit';
import { useAuth } from '../hooks/useAuth';
import 'xterm/css/xterm.css';

export default function SessionPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { session } = useAuth();
  const terminalRef = useRef<HTMLDivElement>(null);
  const [, setTerminal] = useState<Terminal | null>(null);
  const [, setWs] = useState<WebSocket | null>(null);
  const [cost, setCost] = useState(0);
  const [actualSessionId, setActualSessionId] = useState<string | null>(null);

  useEffect(() => {
    if (!session?.access_token) return;

    // If sessionId is "new", create a new session first
    if (sessionId === 'new') {
      const createSession = async () => {
        const apiUrl = import.meta.env.VITE_API_URL || 'https://claude-companion-relay.kyliemckinleydemo.workers.dev';
        const response = await fetch(`${apiUrl}/api/sessions`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            // Let the API use the first available paired device
            // deviceId will be looked up based on the user's paired devices
            recording: true,
          }),
        });
        if (response.ok) {
          const data = await response.json();
          setActualSessionId(data.sessionId);
          // Update URL without reload
          window.history.replaceState(null, '', `/session/${data.sessionId}`);
        }
      };
      createSession();
      return;
    }

    setActualSessionId(sessionId || null);
  }, [sessionId, session, navigate]);

  useEffect(() => {
    if (!terminalRef.current || !session?.access_token || !actualSessionId) return;

    // Initialize xterm.js
    const term = new Terminal({
      cursorBlink: true,
      fontSize: 14,
      fontFamily: 'JetBrains Mono, monospace',
      theme: {
        background: '#1e1e1e',
        foreground: '#d4d4d4',
      },
    });

    const fitAddon = new FitAddon();
    term.loadAddon(fitAddon);
    term.open(terminalRef.current);
    fitAddon.fit();

    setTerminal(term);

    // Connect WebSocket with auth token
    // Use sessionId only - the relay will look up the deviceId from the session
    const relayUrl = import.meta.env.VITE_RELAY_URL || 'wss://claude-companion-relay.kyliemckinleydemo.workers.dev';

    const websocket = new WebSocket(`${relayUrl}/ws?sessionId=${actualSessionId}&token=${session.access_token}&clientType=web`);

    websocket.onopen = () => {
      console.log('WebSocket connected');
    };

    websocket.onmessage = (event) => {
      const message = JSON.parse(event.data);

      if (message.type === 'stdout') {
        term.write(message.data);
      } else if (message.type === 'cost_update') {
        setCost(message.cost);
      }
    };

    websocket.onerror = (error) => {
      console.error('WebSocket error:', error);
      term.write('\r\n\x1b[31mConnection error\x1b[0m\r\n');
    };

    setWs(websocket);

    // Handle terminal input
    term.onData((data) => {
      if (websocket.readyState === WebSocket.OPEN) {
        websocket.send(JSON.stringify({
          type: 'stdin',
          timestamp: Date.now(),
          data: {
            data: data,
            sessionId: actualSessionId,
          },
        }));
      }
    });

    // Cleanup
    return () => {
      term.dispose();
      websocket.close();
    };
  }, [actualSessionId, session]);

  return (
    <div className="h-screen flex flex-col bg-gray-900">
      {/* Header */}
      <div className="bg-gray-800 px-4 py-2 flex justify-between items-center">
        <div className="text-white text-sm">
          Session: {actualSessionId?.substring(0, 8) || 'Loading...'}
        </div>
        <div className="text-green-400 text-sm font-mono">
          Cost: ${cost.toFixed(2)}
        </div>
      </div>

      {/* Terminal */}
      <div ref={terminalRef} className="flex-1" />
    </div>
  );
}
